/**
 * Journey stage durations (days elapsed between two milestones).
 * Pure, framework-agnostic — safe to use in API, web, mobile and tests.
 */

import type { JourneyStageDef } from "../config/work-lines"

const MS_PER_DAY = 86_400_000

/** Complete 24h periods between two instants: 23h59m counts as 0 days. */
export function fullDaysBetween(from: string | Date, to: string | Date): number {
  return Math.floor((new Date(to).getTime() - new Date(from).getTime()) / MS_PER_DAY)
}

/** The first 24h of a stage are free: 24h → 0 stays, 48h → 1, 72h → 2. */
export function stayCount(fullDays: number | null): number {
  return fullDays === null ? 0 : Math.max(0, fullDays - 1)
}

export interface StageDuration {
  stage:      JourneyStageDef
  days:       number | null
  stays:      number
  /** Start recorded but end still missing — `days` counts up to `now`. */
  inProgress: boolean
}

/** Return whether an open journey stage should be counted for a shipment status. */
export function shouldCountOpenJourneyStages(status: string | null | undefined): boolean {
  return status === "activo" || status === "en_curso"
}

/** Sum the stay counts displayed in the journey detail and exported reports. */
export function totalStayCount(durations: StageDuration[]): number {
  return durations.reduce((total, duration) => total + duration.stays, 0)
}

type Timestamps = Partial<Record<JourneyStageDef["from"] | JourneyStageDef["to"], string | Date | null>>

export function computeStageDurations(
  stages: JourneyStageDef[],
  timestamps: Timestamps,
  options: { now?: Date; countOpenStages?: boolean } = {},
): StageDuration[] {
  const now = options.now ?? new Date()
  return stages.map(stage => {
    const from = timestamps[stage.from]
    const to   = timestamps[stage.to]
    const end = to ?? (from && options.countOpenStages ? now : null)
    const days = from && end ? Math.max(0, fullDaysBetween(from, end)) : null
    return { stage, days, stays: stayCount(days), inProgress: days !== null && !to }
  })
}
